import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Boxes, ImageOff, X, Wand2, ShoppingBag, Loader2 } from 'lucide-react';
import { xamplinApi, PURPOSE_BY_ID, type XpEventAsset } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { cn } from '@/lib/cn';
import { toast } from '@/components/ui/Toast';
import { usePagination } from '@/lib/hooks/usePagination';
import { Pagination } from '@/components/ui/Pagination';

/**
 * My Assets (build-v2 F6 + G11) — brand asset library grouped by Asset Purpose,
 * with an asset detail drawer (classify / evidence), a bulk-classify assistant
 * (Atledi Vision) that persists Purpose, and a Xotski merch suggestion.
 */
const PURPOSE_META: Record<string, { color: string }> = {
  SEE: { color: '#5aa9ff' },
  ENGAGE: { color: '#f4b740' },
  TRY: { color: '#4dd28b' },
  ACT: { color: '#ff5a4f' },
};
const PURPOSE_ID: Record<string, number> = { SEE: 1, ENGAGE: 2, TRY: 3, ACT: 4 };
const FILTERS = ['All', 'SEE', 'ENGAGE', 'TRY', 'ACT'] as const;
type Filter = (typeof FILTERS)[number];

export function AssetsPage() {
  const [filter, setFilter] = useState<Filter>('All');
  const [detail, setDetail] = useState<XpEventAsset | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const assetsQ = useQuery({
    queryKey: queryKeys.xamplin.assets,
    queryFn: () => xamplinApi.assets.list(300),
  });
  const assets = assetsQ.data?.items ?? [];
  const unclassified = assets.filter((a) => !a.assetObjetive).length;

  const counts = useMemo(() => {
    const c: Record<string, number> = { All: assets.length, SEE: 0, ENGAGE: 0, TRY: 0, ACT: 0 };
    for (const a of assets) {
      const p = a.assetObjetive ? PURPOSE_BY_ID[a.assetObjetive] : null;
      if (p) c[p] = (c[p] ?? 0) + 1;
    }
    return c;
  }, [assets]);

  const visible = useMemo(
    () =>
      filter === 'All'
        ? assets
        : assets.filter((a) => a.assetObjetive && PURPOSE_BY_ID[a.assetObjetive] === filter),
    [assets, filter],
  );
  const pg = usePagination(visible, 24);

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
          <Boxes className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            My Assets
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {assets.length} assets · por Purpose
          </p>
        </div>
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          className="mono ml-auto flex items-center gap-1.5 rounded-lg border border-[var(--color-xk-accent)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-xk-accent)] hover:bg-[var(--color-accent-10)]"
        >
          <Wand2 className="h-3.5 w-3.5" /> Bulk classify
          {unclassified > 0 && <span className="rounded-full bg-[var(--color-xk-accent)] px-1.5 text-white">{unclassified}</span>}
        </button>
      </div>

      <XotskiSuggestCard />

      {/* Purpose filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f;
          const color = f === 'All' ? 'var(--color-xk-accent)' : PURPOSE_META[f].color;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors',
                active ? 'text-white' : 'text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)]',
              )}
              style={active ? { background: color, borderColor: color } : { borderColor: 'var(--color-xk-border)' }}
            >
              {f}
              <span className={cn('mono', !active && 'text-[var(--color-xk-text-subtle)]')}>{counts[f] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {assetsQ.isLoading && <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando assets…</div>}
      {assetsQ.isError && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudieron cargar los assets.
        </div>
      )}

      {!assetsQ.isLoading && !assetsQ.isError && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {pg.pageItems.map((a) => (
              <AssetCard key={a.id} asset={a} onOpen={() => setDetail(a)} />
            ))}
          </div>
          <Pagination {...pg} />
        </>
      )}

      {detail && <AssetDetailDrawer asset={detail} onClose={() => setDetail(null)} />}
      {bulkOpen && <BulkClassifyModal assets={assets} onClose={() => setBulkOpen(false)} />}
    </div>
  );
}

function AssetCard({ asset, onOpen }: { asset: XpEventAsset; onOpen: () => void }) {
  const purpose = asset.assetObjetive ? PURPOSE_BY_ID[asset.assetObjetive] : null;
  const color = purpose ? PURPOSE_META[purpose].color : 'var(--color-xk-border)';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] text-left transition-colors hover:border-[var(--color-xk-accent)]"
    >
      <div className="aspect-square w-full bg-[var(--color-xk-bg)]">
        {asset.image ? (
          <img
            src={asset.image}
            alt={asset.description ?? 'asset'}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-[var(--color-xk-text-subtle)]">
            <ImageOff className="h-6 w-6" />
          </div>
        )}
      </div>
      {purpose ? (
        <span className="mono absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: color }}>
          {purpose}
        </span>
      ) : (
        <span className="mono absolute left-1.5 top-1.5 rounded-full border border-dashed border-[var(--color-xk-border)] px-2 py-0.5 text-[9px] text-[var(--color-xk-text-subtle)]">
          sin clasificar
        </span>
      )}
    </button>
  );
}

/** Asset detail drawer (G11) — large image, classify control, evidence/condition. */
function AssetDetailDrawer({ asset, onClose }: { asset: XpEventAsset; onClose: () => void }) {
  const qc = useQueryClient();
  const purpose = asset.assetObjetive ? PURPOSE_BY_ID[asset.assetObjetive] : null;
  const classify = useMutation({
    mutationFn: (pid: number) => xamplinApi.assets.classify(asset.id, pid),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Purpose actualizado' });
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.assets });
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo clasificar' }),
  });
  return createPortal(
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-[var(--color-xk-border)] p-4">
          <span className="text-sm font-bold text-[var(--color-xk-lavender)]">Asset</span>
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-4 p-4">
          <div className="aspect-square w-full overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)]">
            {asset.image ? <img src={asset.image} alt="" className="h-full w-full object-cover" /> : null}
          </div>
          <div className="mono text-[11px] text-[var(--color-xk-text-muted)]">{asset.description || 'Sin descripción'}</div>

          <div className="flex flex-col gap-2">
            <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">Asset Purpose</span>
            <div className="flex flex-wrap gap-2">
              {(['SEE', 'ENGAGE', 'TRY', 'ACT'] as const).map((p) => {
                const on = purpose === p;
                return (
                  <button
                    key={p}
                    type="button"
                    disabled={classify.isPending}
                    onClick={() => classify.mutate(PURPOSE_ID[p])}
                    className={cn(
                      'mono rounded-full border px-3 py-1 text-[11px] font-bold transition-colors disabled:opacity-50',
                      on ? 'text-white' : 'text-[var(--color-xk-text-muted)]',
                    )}
                    style={on ? { background: PURPOSE_META[p].color, borderColor: PURPOSE_META[p].color } : { borderColor: 'var(--color-xk-border)' }}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Condition timeline (derived from approvals) */}
          <div className="flex flex-col gap-2">
            <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">Condición</span>
            <div className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2 text-[11px] text-[var(--color-xk-text-muted)]">
              {asset.image ? 'Evidencia RS-1 capturada en campo (foto adjunta).' : 'Sin evidencia.'}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Bulk-classify assistant (G11) — "Atledi Vision": pick a Purpose and apply it
 * to all unclassified assets, persisting each. */
function BulkClassifyModal({ assets, onClose }: { assets: XpEventAsset[]; onClose: () => void }) {
  const qc = useQueryClient();
  const unclassified = assets.filter((a) => !a.assetObjetive);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);

  const apply = async (pid: number) => {
    if (running) return;
    setRunning(true);
    setDone(0);
    // Cap the batch so a demo click doesn't fire thousands of writes.
    const batch = unclassified.slice(0, 50);
    for (let i = 0; i < batch.length; i++) {
      try {
        await xamplinApi.assets.classify(batch[i].id, pid);
      } catch {
        /* keep going — one failure shouldn't abort the batch */
      }
      setDone(i + 1);
    }
    await qc.invalidateQueries({ queryKey: queryKeys.xamplin.assets });
    setRunning(false);
    toast({ tone: 'success', title: `Clasificados ${batch.length} assets` });
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Wand2 className="h-5 w-5 text-[var(--color-xk-accent)]" />
          <h2 className="font-display text-lg font-bold text-[var(--color-xk-lavender)]">Atledi Vision · bulk classify</h2>
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-2 text-sm text-[var(--color-xk-text-muted)]">
          {unclassified.length} assets sin clasificar. Elige un Purpose y se aplicará (hasta 50 por lote).
        </p>
        {running ? (
          <div className="mt-5 flex items-center gap-2 text-sm text-[var(--color-xk-lavender)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Clasificando… {done}
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-2">
            {(['SEE', 'ENGAGE', 'TRY', 'ACT'] as const).map((p) => (
              <button
                key={p}
                type="button"
                disabled={unclassified.length === 0}
                onClick={() => apply(PURPOSE_ID[p])}
                className="mono rounded-lg border px-3 py-2 text-sm font-bold text-white disabled:opacity-40"
                style={{ background: PURPOSE_META[p].color, borderColor: PURPOSE_META[p].color }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Xotski merch suggestion (G11 — prototype XotskiSuggestCard). */
function XotskiSuggestCard() {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-3">
      <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
        <ShoppingBag className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[var(--color-xk-lavender)]">Convierte tus mejores assets en merch</div>
        <div className="mono text-[11px] text-[var(--color-xk-text-muted)]">
          Atledi sugiere productizar los TRY-anchors con mejor MENT como Xotskies de marca.
        </div>
      </div>
      <span className="mono flex-none rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-subtle)]">
        próximamente
      </span>
    </div>
  );
}
